import { encodeAudioEvent, decodeFrame } from './eventstream';
import type { ApiClient } from './api';

/**
 * The microphone half of A.F.R.O.B.O.T: capture, downsample, stream to
 * Transcribe, hand back what was said.
 *
 * The browser gets a signed URL from our API and then talks to Transcribe
 * directly, so the audio never passes through our servers (see the API's
 * listen.ts for why). Nothing is recorded or stored anywhere: the PCM goes
 * out in 100ms frames and is dropped, and what comes back is text.
 *
 * ScriptProcessorNode is deprecated in favour of AudioWorklet, and is used
 * anyway. A worklet is a separate script the page must load, which is a
 * second thing the CSP has to allow for no gain here: this callback does
 * one multiply and a copy per sample, well inside its budget, and it works
 * in every browser the Shell supports today.
 */

const FRAME_SAMPLES = 1600; // 100ms at 16kHz

export interface ListenHandlers {
  /** Called as Transcribe revises what it thinks it heard. */
  onPartial(text: string): void;
  /** Called once per completed phrase. */
  onFinal(text: string): void;
  onError(message: string): void;
}

export interface ListenSession {
  stop(): void;
}

/** Average down to 16kHz. The hardware rate is usually 48kHz, and sending
 *  it at the rate we told Transcribe to expect transcribes chipmunks. */
export function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j] as number;
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

/** Float -1..1 to signed 16-bit little endian, clamped rather than wrapped:
 *  a clipped sample should be loud, not inverted. */
export function toPcm16(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i] as number));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return out;
}

/**
 * Spell our own names the way we spell them.
 *
 * Transcribe hears "Cash Black", "Afro Filiate" and "Equali Shop", which
 * is a reasonable reading of the sound and the wrong string to hand an
 * agent that matches on brand. The alternative is a Transcribe custom
 * vocabulary, which is a second resource to deploy and keep in step with
 * brand/ for three words; this is applied to the transcript, so what the
 * agent receives matches what it would have received typed.
 *
 * Deliberately narrow: our three brands, and nothing else. A general
 * "correct the transcript" pass would be a machine changing what someone
 * said, which is a different and worse thing.
 */
const SPOKEN_BRANDS: [RegExp, string][] = [
  [/\bcash[\s-]?black\b/gi, 'CashBlack'],
  [/\bafro[\s-]?filiate\b/gi, 'Afrofiliate'],
  [/\bafro[\s-]?affiliate\b/gi, 'Afrofiliate'],
  [/\bequali[\s-]?shop\b/gi, 'EqualiShop'],
  [/\bequally[\s-]?shop\b/gi, 'EqualiShop'],
];

export function normaliseBrands(text: string): string {
  return SPOKEN_BRANDS.reduce((out, [pattern, name]) => out.replace(pattern, name), text);
}

export async function startListening(
  api: ApiClient,
  handlers: ListenHandlers,
): Promise<ListenSession> {
  const { url, sampleRate } = await api.post<{ url: string; sampleRate: number }>(
    '/api/listen/url',
    {},
  );

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });

  const context = new AudioContext();
  // Chrome starts an AudioContext suspended unless it can see user
  // activation, and the awaits above (a signed URL, then the permission
  // prompt) are long enough to lose it. A suspended context never fires
  // onaudioprocess, so the microphone light comes on, the button says
  // listening, and not one sample is ever sent.
  if (context.state === 'suspended') await context.resume();

  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(4096, 1, 1);
  // A ScriptProcessorNode only runs when its output reaches the
  // destination, and routing a microphone to the speakers is a howl of
  // feedback in any room where the laptop is not on mute. Silent sink:
  // the graph is connected, the audio is captured, nothing is played.
  const sink = context.createGain();
  sink.gain.value = 0;
  sink.connect(context.destination);

  const socket = new WebSocket(url);
  socket.binaryType = 'arraybuffer';

  let pending: number[] = [];
  let heardAnything = false;
  let framesSent = 0;
  let diagnoseClose = true;
  let opened = false;
  let captureReleased = false;
  let streamEnded = false;
  let closeTimer: number | undefined;

  /** Let go of the microphone. Separate from ending the stream because
   *  every exit path must run this - the 5 September review found the
   *  remote-close path leaving the mic hot with the recording light on
   *  while the panel said the session was over. */
  const releaseCapture = () => {
    if (captureReleased) return;
    captureReleased = true;
    processor.disconnect();
    source.disconnect();
    for (const track of stream.getTracks()) track.stop();
    void context.close();
  };

  const endStream = () => {
    if (streamEnded) return;
    streamEnded = true;
    if (socket.readyState === WebSocket.OPEN) {
      // Flush the tail of the buffer first: up to 100ms of the last word
      // sits below the frame threshold, and dropping it clips speech.
      if (pending.length > 0) {
        socket.send(encodeAudioEvent(toPcm16(Float32Array.from(pending))));
        pending = [];
      }
      // The empty frame is Transcribe's end-of-stream. The transcript of
      // what was just flushed arrives a round trip AFTER it, so the
      // socket must outlive this call: close() in the same tick was the
      // bug that made "Stop listening" silently discard the question.
      socket.send(encodeAudioEvent(new Uint8Array(0)));
      closeTimer = window.setTimeout(() => socket.close(), 2000);
    } else if (socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
  };

  const stop = () => {
    diagnoseClose = false;
    releaseCapture();
    endStream();
  };

  processor.onaudioprocess = (event) => {
    if (socket.readyState !== WebSocket.OPEN || streamEnded) return;
    const reduced = downsample(event.inputBuffer.getChannelData(0), context.sampleRate, sampleRate);
    pending.push(...reduced);
    while (pending.length >= FRAME_SAMPLES) {
      const frame = Float32Array.from(pending.slice(0, FRAME_SAMPLES));
      pending = pending.slice(FRAME_SAMPLES);
      socket.send(encodeAudioEvent(toPcm16(frame)));
      framesSent++;
    }
  };

  socket.onmessage = (event) => {
    // Deliberately NO closed guard here: after a stop the socket stays up
    // briefly so the final transcript of the flushed audio can land. The
    // caller decides when it has its question and ignores the rest -
    // guarding here is how a manual stop ate the transcript.
    const frame = decodeFrame(event.data as ArrayBuffer);
    if (frame.error) {
      handlers.onError(frame.error);
      stop();
      window.clearTimeout(closeTimer);
      socket.close();
      return;
    }
    if (frame.text === undefined || frame.text === '') return;
    heardAnything = true;
    if (frame.partial) {
      handlers.onPartial(frame.text);
    } else {
      handlers.onFinal(frame.text);
      // A final after end-of-stream is the last there will be: no need
      // to hold the socket open for the rest of the grace period.
      if (streamEnded) {
        window.clearTimeout(closeTimer);
        socket.close();
      }
    }
  };

  socket.onerror = () => {
    if (!opened) return; // the handshake promise below reports this one
    handlers.onError('The connection to the transcriber dropped.');
    stop();
  };

  // A socket that closes having transcribed nothing is the failure that is
  // hardest to report, because from the outside it looks identical to
  // saying nothing. The close code and the frame count separate the three
  // cases - rejected before audio, audio sent and not understood, or a
  // microphone that produced no sound at all - and say which out loud
  // rather than leaving a dead button.
  socket.onclose = (event) => {
    window.clearTimeout(closeTimer);
    const report = opened && diagnoseClose && !heardAnything;
    // However this session ended, it must end holding no microphone.
    releaseCapture();
    if (!report) return;
    if (event.code !== 1000 && event.code !== 1005) {
      handlers.onError(`The transcriber closed the connection (code ${event.code}).`);
    } else if (framesSent === 0) {
      handlers.onError('No audio reached the transcriber. Check the microphone Chrome is using.');
    } else {
      handlers.onError('Nothing was heard. Try again, a little closer to the microphone.');
    }
  };

  // Resolve only once the socket is genuinely open. Reporting "listening"
  // during the handshake invites words the stream never carried, which
  // clipped the first words of every quick speaker.
  await new Promise<void>((resolve, reject) => {
    const fail = (why: string) => {
      releaseCapture();
      try {
        socket.close();
      } catch {
        // Closing a socket that never opened can throw; there is nothing
        // further to release either way.
      }
      reject(new Error(why));
    };
    const timer = window.setTimeout(() => fail('The transcriber did not answer in time.'), 10_000);
    socket.addEventListener(
      'open',
      () => {
        window.clearTimeout(timer);
        opened = true;
        source.connect(processor);
        processor.connect(sink);
        resolve();
      },
      { once: true },
    );
    socket.addEventListener(
      'close',
      () => {
        window.clearTimeout(timer);
        fail('The transcriber refused the connection.');
      },
      { once: true },
    );
  });

  return { stop };
}
